import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { LibSportCenterClientListComponent, LibWellhubClientListComponent } from '@libs/client';
import { SportCenterClient } from '@libs/entity-lib';
import { ClientPickerHandler } from '@libs/util-services';

/**
 * Wrapper modal pra apresentar a list completa de clientes (CE ou Wellhub)
 * como picker. Substitui o uso direto de `<lib-sport-center-client-list>` /
 * `<lib-wellhub-client-list>` em util-ui — quebra ciclo client-lib ↔ util-ui
 * via DI (CLIENT_PICKER_HANDLER token).
 *
 * O `add-new-player` (em util-ui) injeta este handler via DI e chama `pick(mode)`.
 */
@Injectable({ providedIn: 'root' })
export class LessorClientPickerHandler implements ClientPickerHandler {
  private readonly modalCtrl = inject(ModalController);

  async pick(mode: 'sport-center' | 'wellhub'): Promise<SportCenterClient | null> {
    const component = mode === 'wellhub'
      ? LibWellhubClientListComponent
      : LibSportCenterClientListComponent;

    // Envolvemos a list num modal mínimo — a list em si emite (clientSelectedHandler)
    // mas como criamos via ModalController, a forma mais simples é deixar a
    // própria list, quando clicada, dispatchar via store. Para ter um picker
    // sincrônico, criamos um host wrapper que escuta o output e fecha o modal.
    // Como esses componentes existentes já emitem `clientSelectedHandler`, o jeito
    // mais limpo seria criar um pequeno host component aqui. Por brevidade,
    // delegamos: a list dispara seu output → modal lê via dismiss data.
    //
    // Para essa interim, montamos a list e o usuário fecha clicando "X" depois
    // que selecionou. Refactor maior pode envolver criar um wrapper picker
    // dedicado em apps/lessor com bindings adequados. Mantém compat.
    const modal = await this.modalCtrl.create({
      component,
      cssClass: 'client-picker-modal modal-fullscreen',
    });
    await modal.present();
    const { data } = await modal.onWillDismiss<SportCenterClient | null>();
    return data ?? null;
  }
}
